import type { ConnectorRun, ProjectRun, RunsReportResponse, RunStatus } from "../types";

export const STATUS_COLORS: Record<
  RunStatus,
  {
    label: string;
    variant: "success" | "info" | "destructive" | "warning";
    pulse?: boolean;
  }
> = {
  completed: { label: "Completed", variant: "success" },
  continued: { label: "Continued", variant: "info" },
  started: {
    label: "Running",
    variant: "info",
    pulse: true,
  },
  failed: { label: "Failed", variant: "destructive" },
  timed_out: { label: "Timed Out", variant: "warning" },
  abandoned: { label: "Abandoned", variant: "warning" },
};

const STATUS_ORDER: RunStatus[] = [
  "completed",
  "continued",
  "started",
  "failed",
  "timed_out",
  "abandoned",
];

export function formatDay(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

export function getDateKey(dateStr: string): string {
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function getDurationSeconds(startedAt: string, endedAt: string | null): number | null {
  if (!endedAt) return null;
  return Math.round((new Date(endedAt).getTime() - new Date(startedAt).getTime()) / 1000);
}

export function formatDuration(seconds: number | null): string {
  if (seconds === null) return "–";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  }
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${hrs}h ${mins}m ${secs}s` : `${hrs}h ${mins}m`;
}

export function getPagesFetched(tr: { startPage: number | null; endPage: number | null }): number {
  if (tr.startPage !== null && tr.endPage !== null) return tr.endPage - tr.startPage + 1;
  return 0;
}

export interface ProjectDayGroup {
  projectId: string;
  projectName: string;
  dateKey: string;
  runs: ProjectRun[];
}

export interface ProjectGroup {
  projectId: string;
  projectName: string;
  days: ProjectDayGroup[];
}

export function groupByProject(runs: ProjectRun[]): ProjectGroup[] {
  const dayGroups = new Map<string, ProjectDayGroup>();
  for (const run of runs) {
    const dateKey = getDateKey(run.startedAt);
    const groupKey = `${run.projectId}-${dateKey}`;
    let group = dayGroups.get(groupKey);
    if (!group) {
      group = { projectId: run.projectId, projectName: run.project.name, dateKey, runs: [] };
      dayGroups.set(groupKey, group);
    }
    group.runs.push(run);
  }

  for (const group of dayGroups.values()) {
    group.runs.sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  }

  const byProject = new Map<string, ProjectGroup>();
  for (const day of dayGroups.values()) {
    let project = byProject.get(day.projectId);
    if (!project) {
      project = { projectId: day.projectId, projectName: day.projectName, days: [] };
      byProject.set(day.projectId, project);
    }
    project.days.push(day);
  }

  const result = Array.from(byProject.values());
  result.sort((a, b) => a.projectName.localeCompare(b.projectName));
  for (const project of result) {
    project.days.sort((a, b) => b.dateKey.localeCompare(a.dateKey));
  }

  return result;
}

export function flattenConnectorRuns(
  projectRuns: ProjectRun[],
): { connectorRun: ConnectorRun; projectRunId: number; displayName: string }[] {
  const result: { connectorRun: ConnectorRun; projectRunId: number; displayName: string }[] = [];
  for (const projectRun of projectRuns) {
    for (const connectorRun of projectRun.connectorRuns) {
      const displayName =
        projectRun.functionName && projectRun.functionName.trim().length > 0
          ? projectRun.functionName
          : connectorRun.connector.name;
      result.push({ connectorRun, projectRunId: projectRun.id, displayName });
    }
  }
  result.sort(
    (a, b) =>
      new Date(b.connectorRun.startedAt).getTime() - new Date(a.connectorRun.startedAt).getTime(),
  );
  return result;
}

export interface StatusColumn {
  label: string;
  segments: { status: RunStatus; variant: string; pct: number }[];
}

export function buildStatusColumn(statuses: RunStatus[], label: string): StatusColumn {
  const counts = new Map<RunStatus, number>();
  for (const s of statuses) counts.set(s, (counts.get(s) ?? 0) + 1);
  const total = statuses.length;
  const segments =
    total === 0
      ? []
      : STATUS_ORDER.filter((s) => (counts.get(s) ?? 0) > 0).map((status) => ({
          status,
          variant: STATUS_COLORS[status].variant,
          pct: ((counts.get(status) ?? 0) / total) * 100,
        }));
  return { label, segments };
}

export function sumDurationAndPages(connectorRuns: { connectorRun: ConnectorRun }[]) {
  let totalSeconds = 0;
  let totalPages = 0;
  for (const { connectorRun } of connectorRuns) {
    const dur = getDurationSeconds(connectorRun.startedAt, connectorRun.endedAt);
    if (dur !== null) totalSeconds += dur;
    for (const tr of connectorRun.tableRuns) {
      totalPages += getPagesFetched(tr);
    }
  }
  return { totalSeconds, totalPages };
}

export function sumRecords(runs: ProjectRun[]) {
  let inserted = 0,
    fetched = 0,
    skipped = 0;
  for (const run of runs) {
    for (const cr of run.connectorRuns) {
      for (const tr of cr.tableRuns) {
        inserted += tr.recordsInserted;
        fetched += tr.recordsFetched;
        skipped += tr.recordsSkipped;
      }
    }
  }
  return { totalInserted: inserted, totalFetched: fetched, totalSkipped: skipped };
}

export type ModeFilter = "full" | "incremental" | null;

export function parseModeFilter(modeParam: string | null): ModeFilter {
  if (modeParam === "full" || modeParam === "incremental") return modeParam;
  return null;
}

export function filterRunsByMode(runs: ProjectRun[], mode: ModeFilter): ProjectRun[] {
  if (!mode) return runs;
  const filtered: ProjectRun[] = [];
  for (const run of runs) {
    const connectorRuns: ConnectorRun[] = [];
    for (const cr of run.connectorRuns) {
      const tableRuns = cr.tableRuns.filter((t) => t.mode === mode);
      if (tableRuns.length === 0) continue;
      connectorRuns.push({ ...cr, tableRuns });
    }
    if (connectorRuns.length === 0) continue;
    filtered.push({ ...run, connectorRuns });
  }
  return filtered;
}

export function filterByConnectorRunId(runs: ProjectRun[], connectorRunId: number): ProjectRun[] {
  return runs
    .map((run) => {
      const crs = run.connectorRuns.filter((cr) => cr.id === connectorRunId);
      if (crs.length === 0) return null;
      return { ...run, connectorRuns: crs };
    })
    .filter((r): r is ProjectRun => r !== null);
}

export function buildFilterParts(
  data: RunsReportResponse,
  filterProjectId: string | null,
  filterDate: string | null,
  filterMode: ModeFilter,
  connectorRunId: number | null,
): string[] {
  const parts: string[] = [];
  if (filterProjectId) {
    const project = data.runs.find((r) => r.projectId === filterProjectId);
    if (project) parts.push(`Project: ${project.project.name}`);
  }
  if (filterDate) parts.push(`Date: ${filterDate}`);
  if (filterMode) parts.push(`Mode: ${filterMode}`);
  if (connectorRunId) {
    const match = data.runs
      .flatMap((run) =>
        run.connectorRuns.map((cr) => ({
          cr,
          name:
            run.functionName && run.functionName.trim().length > 0
              ? run.functionName
              : cr.connector.name,
        })),
      )
      .find((entry) => entry.cr.id === connectorRunId);
    if (match) parts.push(`Connector: ${match.name} (${match.cr.id})`);
    else parts.push(`ConnectorRun: ${connectorRunId}`);
  }
  return parts;
}

export interface AggregatedTableRun {
  tableName: string;
  minStartedAt: string;
  maxEndedAt: string | null;
  minStartPage: number | null;
  maxEndPage: number | null;
  totalInserted: number;
  totalFetched: number;
  totalSkipped: number;
  finalStatus: RunStatus;
  runCount: number;
  mode: string | null;
  sourceFileName: string | null;
  sourceFileTimestamp: string | null;
  freshnessStatus: "ok" | "stale-file" | null;
}

export function aggregateTableRuns(tableRuns: import("../types").TableRun[]): AggregatedTableRun[] {
  const byTable = new Map<string, import("../types").TableRun[]>();
  for (const run of tableRuns) {
    let list = byTable.get(run.tableName);
    if (!list) {
      list = [];
      byTable.set(run.tableName, list);
    }
    list.push(run);
  }

  const result: AggregatedTableRun[] = [];
  for (const [tableName, runs] of byTable) {
    const sorted = [...runs].sort(
      (a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime(),
    );
    const minStartedAt = sorted[0].startedAt;
    const endedAts = sorted.map((r) => r.endedAt).filter((e): e is string => e !== null);
    const maxEndedAt =
      endedAts.length > 0 ? endedAts.reduce((a, b) => (new Date(a) > new Date(b) ? a : b)) : null;
    const startPages = sorted.map((r) => r.startPage).filter((p): p is number => p !== null);
    const endPages = sorted.map((r) => r.endPage).filter((p): p is number => p !== null);
    const lastRun = sorted[sorted.length - 1];

    result.push({
      tableName,
      minStartedAt,
      maxEndedAt,
      minStartPage: startPages.length > 0 ? Math.min(...startPages) : null,
      maxEndPage: endPages.length > 0 ? Math.max(...endPages) : null,
      totalInserted: runs.reduce((sum, r) => sum + r.recordsInserted, 0),
      totalFetched: runs.reduce((sum, r) => sum + r.recordsFetched, 0),
      totalSkipped: runs.reduce((sum, r) => sum + r.recordsSkipped, 0),
      finalStatus: lastRun.status,
      runCount: runs.length,
      mode: lastRun.mode,
      sourceFileName: lastRun.sourceFileName ?? null,
      sourceFileTimestamp: lastRun.sourceFileTimestamp ?? null,
      freshnessStatus: lastRun.freshnessStatus ?? null,
    });
  }

  result.sort((a, b) => a.tableName.localeCompare(b.tableName));
  return result;
}

export function getConnectorTotals(connectorRun: ConnectorRun) {
  const totalInserted = connectorRun.tableRuns.reduce((sum, r) => sum + r.recordsInserted, 0);
  const totalFetched = connectorRun.tableRuns.reduce((sum, r) => sum + r.recordsFetched, 0);
  const totalSkipped = connectorRun.tableRuns.reduce((sum, r) => sum + r.recordsSkipped, 0);
  const tableCount = new Set(connectorRun.tableRuns.map((t) => t.tableName)).size;
  return { totalInserted, totalFetched, totalSkipped, tableCount };
}
